import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Bot, Send, Square, Check, X, Loader2, Wifi, WifiOff, ChevronRight, ChevronDown, MessageCircleQuestion, Slash, Zap, Terminal, FileText, LogIn, LogOut, ExternalLink, Key, Shield, MessageSquarePlus } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import Panel from './Panel'
import { useClaudeSession, type ChatMessage, type ContentBlock, type PermissionRequest, type QuestionRequest, type QuestionItem, type ToolProgress, type ImageAttachment, type LoginState } from '../../hooks/useClaudeSession'

// ─── Image Helpers ───────────────────────────────────────────────────────────

// Image formats supported by Claude API as image content blocks
const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

// Anthropic rejects images > this on either axis for many-image requests.
// Dashboard-side downscale keeps the user's session from getting poisoned
// by a single large screenshot.
const MAX_IMAGE_DIM = 2000

function decodeImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
    img.src = url
  })
}

function drawScaled(img: HTMLImageElement, maxDim: number): HTMLCanvasElement {
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const scale = Math.min(1, maxDim / Math.max(w, h))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w * scale))
  canvas.height = Math.max(1, Math.round(h * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas
}

async function readFileAsBase64(file: File): Promise<{ base64: string; mediaType: string }> {
  // SVG: text payload, no raster dimension limit applies. Pass through as-is;
  // the bridge converts it to a text block downstream.
  if (file.type === 'image/svg+xml') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve({ base64, mediaType: file.type })
      }
      reader.onerror = () => reject(new Error('Failed to read SVG'))
      reader.readAsDataURL(file)
    })
  }

  const img = await decodeImage(file)
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  const needsDownscale = Math.max(w, h) > MAX_IMAGE_DIM
  const needsTranscode = !SUPPORTED_IMAGE_TYPES.has(file.type)

  // Fast path: already within limits AND in a supported format → keep original bytes.
  // Avoids re-encoding (lossy for JPEG, wastes CPU for PNG).
  if (!needsDownscale && !needsTranscode) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve({ base64, mediaType: file.type })
      }
      reader.onerror = () => reject(new Error('Failed to read image'))
      reader.readAsDataURL(file)
    })
  }

  // Draw to canvas, exporting as JPEG (smaller) for non-transparency-sensitive
  // formats, otherwise PNG. Target format must be in SUPPORTED_IMAGE_TYPES.
  const canvas = drawScaled(img, MAX_IMAGE_DIM)
  const targetMime = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png'
  const quality = targetMime === 'image/jpeg' ? 0.92 : undefined
  const dataUrl = canvas.toDataURL(targetMime, quality)
  const base64 = dataUrl.split(',')[1]
  if (needsDownscale) {
    console.log(`[claude-panel] Downscaled ${w}x${h} → ${canvas.width}x${canvas.height} (${targetMime})`)
  }
  return { base64, mediaType: targetMime }
}

// ─── Typewriter Hook ─────────────────────────────────────────────────────────

/**
 * Smooths chunky streaming text by revealing characters gradually.
 * Buffers incoming text and reveals it at a steady rate using requestAnimationFrame.
 */
function useTypewriter(source: string, charsPerFrame = 3): string {
  const [displayed, setDisplayed] = useState('')
  const targetRef = useRef('')
  const displayedRef = useRef('')
  const rafRef = useRef<number>(0)

  // When source resets (new message), reset displayed immediately
  useEffect(() => {
    if (source === '') {
      setDisplayed('')
      displayedRef.current = ''
      targetRef.current = ''
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }
    targetRef.current = source
  }, [source])

  useEffect(() => {
    const tick = () => {
      const target = targetRef.current
      const current = displayedRef.current
      if (current.length < target.length) {
        const next = target.slice(0, current.length + charsPerFrame)
        displayedRef.current = next
        setDisplayed(next)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [charsPerFrame])

  return displayed
}

interface ClaudePanelProps {
  appName: string
  onClose?: () => void
  closeable?: boolean
  isMaximized?: boolean
  onMaximize?: () => void
}

// Generate a stable session ID per app (persists across re-renders, not across page loads)
// Permission modes — cycled with Shift+Tab
const PERMISSION_MODES = ['bypassPermissions', 'acceptEdits', 'default', 'plan'] as const
const MODE_LABELS: Record<string, string> = { bypassPermissions: 'Full Auto', acceptEdits: 'Accept Edits', default: 'Ask First', plan: 'Plan' }

// Deterministic session ID — all tabs for the same workspace share one bridge session
function useSessionId(appName: string): string {
  return useMemo(() => `workspace-${appName}`, [appName])
}

export default function ClaudePanel({
  appName,
  onClose,
  closeable = true,
  isMaximized,
  onMaximize,
}: ClaudePanelProps) {
  const sessionId = useSessionId(appName)
  const {
    connected,
    cliConnected,
    messages,
    sessionState,
    pendingPermissions,
    pendingQuestions,
    streamingText,
    streamingThinking,
    isStreaming,
    isCompacting,
    activeTools,
    sendMessage,
    respondToPermission,
    respondToQuestion,
    interrupt,
    setModel,
    setPermissionMode,
    loginState,
    startLogin,
    submitLoginCode,
    logout,
    newSession,
  } = useClaudeSession(appName, sessionId)

  const [input, setInput] = useState('')
  const [slashMenuIndex, setSlashMenuIndex] = useState(0)
  const [images, setImages] = useState<ImageAttachment[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const slashMenuRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const prevInputRef = useRef('')

  // Input history (readline-style up/down arrow)
  const inputHistoryRef = useRef<string[]>([])
  const historyIndexRef = useRef(-1)
  const savedInputRef = useRef('')

  // Stall detection — warn when no response arrives after sending
  const [stalled, setStalled] = useState(false)
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Build slash command list from session state (depend on arrays, not whole object)
  const slashCommands = sessionState?.slash_commands
  const slashSkills = sessionState?.skills
  const allCommands = useMemo(() => {
    const cmds: { name: string; type: 'command' | 'skill' }[] = []
    if (slashCommands) for (const c of slashCommands) cmds.push({ name: c, type: 'command' })
    if (slashSkills) for (const s of slashSkills) cmds.push({ name: s, type: 'skill' })
    return cmds
  }, [slashCommands, slashSkills])

  // Filter commands based on current input.
  // Use a ref to cache the last result and avoid creating new array references
  // on every render (which would destabilize handleKeyDown's useCallback).
  const filteredCommandsRef = useRef<{ name: string; type: 'command' | 'skill' }[]>([])
  const lastFilterInputRef = useRef('')
  if (input !== lastFilterInputRef.current) {
    lastFilterInputRef.current = input
    const m = input.match(/^\/(\S*)$/)
    if (!m) {
      filteredCommandsRef.current = []
    } else {
      const q = m[1].toLowerCase()
      filteredCommandsRef.current = q ? allCommands.filter(c => c.name.toLowerCase().startsWith(q)) : allCommands
    }
  }
  const filteredCommands = filteredCommandsRef.current
  const slashMenuVisible = filteredCommands.length > 0

  // Reset menu index when input changes
  if (input !== prevInputRef.current) {
    prevInputRef.current = input
    setSlashMenuIndex(0)
  }

  // Smooth streaming text via typewriter effect
  const smoothText = useTypewriter(streamingText)

  // Scroll active menu item into view
  useEffect(() => {
    if (!slashMenuVisible || !slashMenuRef.current) return
    const item = slashMenuRef.current.querySelector(`[data-cmd-index="${slashMenuIndex}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [slashMenuIndex, slashMenuVisible])

  // Clear stall indicator when any response arrives
  useEffect(() => {
    if (isStreaming || streamingText || streamingThinking || activeTools.size > 0) {
      setStalled(false)
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null }
    }
  }, [isStreaming, streamingText, streamingThinking, activeTools])

  // Start stall timer when last message is a user_message (manual or auto-build),
  // clear when a response arrives
  useEffect(() => {
    if (messages.length > 0) {
      const last = messages[messages.length - 1]
      if (last.type === 'assistant' || last.type === 'result') {
        setStalled(false)
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null }
      } else if (last.type === 'user_message' && !stallTimerRef.current) {
        // User message with no response yet — start stall timer
        setStalled(false)
        stallTimerRef.current = setTimeout(() => setStalled(true), 15000)
      }
    }
  }, [messages])

  // Auto-scroll on new messages and streaming updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, pendingPermissions, pendingQuestions, smoothText])

  const selectCommand = useCallback((cmd: { name: string }) => {
    setInput(`/${cmd.name} `)
    textareaRef.current?.focus()
  }, [])

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    const text = input.trim()
    if (!text) return
    // Save to input history (avoid duplicating the last entry)
    const hist = inputHistoryRef.current
    if (hist.length === 0 || hist[hist.length - 1] !== text) {
      hist.push(text)
      if (hist.length > 100) hist.shift()
    }
    historyIndexRef.current = -1
    savedInputRef.current = ''
    sendMessage(text, images.length > 0 ? images : undefined)
    setInput('')
    setImages([])
    // Start stall timer — if no response in 15s, warn user
    setStalled(false)
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current)
    stallTimerRef.current = setTimeout(() => setStalled(true), 15000)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [input, images, sendMessage])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const newImages: ImageAttachment[] = []
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue
      const { base64, mediaType } = await readFileAsBase64(file)
      newImages.push({ media_type: mediaType, data: base64 })
    }
    setImages(prev => [...prev, ...newImages])
    e.target.value = ''
  }, [])

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    const newImages: ImageAttachment[] = []
    for (const item of Array.from(items)) {
      if (!item.type.startsWith('image/')) continue
      const file = item.getAsFile()
      if (!file) continue
      const { base64, mediaType } = await readFileAsBase64(file)
      newImages.push({ media_type: mediaType, data: base64 })
    }
    if (newImages.length > 0) {
      e.preventDefault()
      setImages(prev => [...prev, ...newImages])
    }
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMenuVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashMenuIndex(prev => (prev + 1) % filteredCommands.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashMenuIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        selectCommand(filteredCommands[slashMenuIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setInput('')
        return
      }
    }
    // Input history navigation (readline-style) — only when not in slash menu
    const el = e.currentTarget
    if (e.key === 'ArrowUp' && el.selectionStart === 0 && el.selectionEnd === 0) {
      const hist = inputHistoryRef.current
      if (hist.length === 0) return
      e.preventDefault()
      if (historyIndexRef.current === -1) {
        // Entering history — save current input
        savedInputRef.current = input
        historyIndexRef.current = hist.length - 1
      } else if (historyIndexRef.current > 0) {
        historyIndexRef.current--
      }
      setInput(hist[historyIndexRef.current])
      return
    }
    if (e.key === 'ArrowDown' && historyIndexRef.current >= 0 && el.selectionStart === el.value.length) {
      e.preventDefault()
      const hist = inputHistoryRef.current
      if (historyIndexRef.current < hist.length - 1) {
        historyIndexRef.current++
        setInput(hist[historyIndexRef.current])
      } else {
        // Back to saved input
        historyIndexRef.current = -1
        setInput(savedInputRef.current)
      }
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit, input, slashMenuVisible, filteredCommands, slashMenuIndex, selectCommand])

  // Auto-resize textarea
  const handleTextareaInput = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 150) + 'px'
  }, [])

  // Determine if Claude is actively working
  const isWorking = useMemo(() => {
    if (isStreaming || isCompacting || activeTools.size > 0) return true
    if (messages.length === 0) return false
    const last = messages[messages.length - 1]
    return last.type === 'assistant' || last.type === 'user_message' || pendingPermissions.length > 0
  }, [messages, pendingPermissions, isStreaming, isCompacting, activeTools])

  const currentMode = sessionState?.permissionMode || 'bypassPermissions'

  // Group messages into top-level and subagent threads
  const { topLevel: topLevelMessages, childrenByParent, agentMeta } = useMemo(() => {
    const meta = new Map<string, { description: string; agentType: string }>()
    const children = new Map<string, ChatMessage[]>()
    const top: ChatMessage[] = []

    // First pass: extract Agent/Task tool metadata
    for (const msg of messages) {
      if (msg.type !== 'assistant') continue
      for (const block of msg.message?.content || []) {
        if (block.type === 'tool_use' && (block.name === 'Agent' || block.name === 'Task') && block.id) {
          meta.set(block.id, {
            description: (block.input?.description as string) || 'Subagent',
            agentType: (block.input?.subagent_type as string) || '',
          })
        }
      }
    }

    // Collect all parent_tool_use_ids so we can detect orphaned children
    // (whose parent Agent block was lost in message merging)
    const allParentIds = new Set<string>()
    for (const msg of messages) {
      const parentId = (msg as { parent_tool_use_id?: string | null }).parent_tool_use_id
      if (parentId) allParentIds.add(parentId)
    }

    // Register unknown parents (orphaned) so children still get grouped
    for (const parentId of allParentIds) {
      if (!meta.has(parentId)) {
        meta.set(parentId, { description: 'Subagent', agentType: '' })
      }
    }

    // Second pass: partition by parent_tool_use_id
    for (const msg of messages) {
      const parentId = (msg as { parent_tool_use_id?: string | null }).parent_tool_use_id
      if (parentId && meta.has(parentId)) {
        let arr = children.get(parentId)
        if (!arr) { arr = []; children.set(parentId, arr) }
        arr.push(msg)
      } else {
        top.push(msg)
      }
    }

    return { topLevel: top, childrenByParent: children, agentMeta: meta }
  }, [messages])

  const [modeMenuOpen, setModeMenuOpen] = useState(false)

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isWorking) {
        e.preventDefault()
        interrupt()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isWorking, interrupt])

  // Show login UI when bridge reports no credentials, login flow is active,
  // or the last result message indicates auth failure
  const needsLogin = useMemo(() => {
    // Active login flow (awaiting URL, code input, submitting)
    if (loginState.status !== 'idle' && loginState.status !== 'complete') return true
    // Bridge reported no credentials file
    if (loginState.error === 'not_authenticated') return true
    // Detect auth failure from the last result message (fallback for stale/expired credentials)
    if (messages.length > 0) {
      const last = messages[messages.length - 1]
      if (last.type === 'result' && last.data?.result && typeof last.data.result === 'string'
        && (last.data.result.includes('Not logged in') || last.data.result.includes('Please run /login'))) {
        return true
      }
    }
    return false
  }, [loginState, messages])

  const [modelMenuOpen, setModelMenuOpen] = useState(false)
  const [logoutMenuOpen, setLogoutMenuOpen] = useState(false)
  const [newChatMenuOpen, setNewChatMenuOpen] = useState(false)
  const MODELS = ['opus', 'sonnet', 'haiku']

  const statusIcon = connected
    ? (cliConnected ? <Wifi className="w-3 h-3 text-green-500" /> : <Loader2 className="w-3 h-3 text-yellow-500 animate-spin" />)
    : <WifiOff className="w-3 h-3 text-red-500" />

  const statusText = connected
    ? (cliConnected ? (sessionState?.model || 'Connected') : 'Starting Claude...')
    : 'Disconnected'

  return (
    <Panel
      title="Claude"
      icon={<Bot className="w-4 h-4" />}
      onClose={onClose}
      closeable={closeable}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      leftExtra={
        /* Session actions — sit next to the "Claude" title on the left so they're
           muscle-memory-close to the chat, away from the model/mode selectors. */
        connected && !needsLogin ? (
          <>
            {/* New chat — archives current session, clears feed */}
            <div className="relative">
              <button
                onClick={() => setNewChatMenuOpen(p => !p)}
                className="flex items-center p-1 rounded hover:bg-secondary/50 hover:text-foreground transition-colors"
                title="New chat"
                aria-label="Start a new chat"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" />
              </button>
              {newChatMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNewChatMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg p-3 w-60 space-y-2">
                    <p className="text-xs text-foreground">Start a new chat?</p>
                    <p className="text-xs text-muted-foreground">
                      The current conversation will be archived. You can recover it from
                      <code className="mx-1 px-1 rounded bg-secondary/50 text-[10px]">~/.claude/projects/</code>
                      if needed.
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => setNewChatMenuOpen(false)}
                        className="px-2 py-1 rounded text-xs hover:bg-secondary/50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { newSession(); setNewChatMenuOpen(false) }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs hover:bg-primary/90 transition-colors"
                      >
                        <MessageSquarePlus className="w-3 h-3" />
                        New chat
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Sign out — wipes stored credentials so the user can force re-auth
                when Anthropic returns a stale 401 and the CLI can't recover. */}
            <div className="relative">
              <button
                onClick={() => setLogoutMenuOpen(p => !p)}
                className="flex items-center p-1 rounded hover:bg-secondary/50 hover:text-foreground transition-colors"
                title="Sign out of Claude"
                aria-label="Sign out of Claude"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
              {logoutMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLogoutMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg p-3 w-56 space-y-2">
                    <p className="text-xs text-foreground">Sign out of Claude?</p>
                    <p className="text-xs text-muted-foreground">
                      This removes stored credentials. You'll need to sign in again to continue.
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => setLogoutMenuOpen(false)}
                        className="px-2 py-1 rounded text-xs hover:bg-secondary/50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { logout(); setLogoutMenuOpen(false) }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-destructive text-destructive-foreground text-xs hover:bg-destructive/90 transition-colors"
                      >
                        <LogOut className="w-3 h-3" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : null
      }
      extra={
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {isCompacting && (
            <span className="flex items-center gap-1 text-yellow-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Compacting
            </span>
          )}
          {statusIcon}
          {/* Model switcher dropdown */}
          {cliConnected && sessionState?.model ? (
            <div className="relative">
              <button
                onClick={() => setModelMenuOpen(p => !p)}
                className="flex items-center gap-0.5 hover:text-foreground transition-colors"
              >
                <span>{statusText}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {modelMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setModelMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 min-w-[120px]">
                    {MODELS.map(m => (
                      <button
                        key={m}
                        onClick={() => { setModel(m); setModelMenuOpen(false) }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 transition-colors ${
                          sessionState.model?.includes(m) ? 'text-primary font-medium' : ''
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <span>{statusText}</span>
          )}
          {/* Permission mode dropdown */}
          {cliConnected && (
            <>
              <span className="text-border">·</span>
              <div className="relative">
                <button
                  onClick={() => setModeMenuOpen(p => !p)}
                  className="flex items-center gap-0.5 hover:text-foreground transition-colors"
                >
                  <Shield className="w-3 h-3" />
                  <span>{MODE_LABELS[currentMode] || currentMode}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {modeMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setModeMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 rounded-lg border border-border bg-card shadow-lg py-1 min-w-[140px]">
                      {PERMISSION_MODES.map(m => (
                        <button
                          key={m}
                          onClick={() => { setPermissionMode(m); setModeMenuOpen(false) }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 transition-colors ${
                            currentMode === m ? 'text-primary font-medium' : ''
                          }`}
                        >
                          {MODE_LABELS[m]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col h-full bg-background">
        {/* Connection status banners */}
        {connected && !cliConnected && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-b border-yellow-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Claude CLI disconnected — reconnecting...</span>
          </div>
        )}
        {!connected && (
          <div className="flex items-center justify-between px-3 py-1.5 text-xs bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-b border-yellow-500/20">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Reconnecting...</span>
            </div>
          </div>
        )}

        {/* Message Feed */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin">
          {/* Welcome Banner — shown before any messages */}
          {messages.length === 0 && !needsLogin && (
            <WelcomeBanner
              cliConnected={cliConnected}
              sessionState={sessionState}
            />
          )}

          {/* Login card — shown prominently when no messages and auth needed */}
          {messages.length === 0 && needsLogin && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Bot className="w-10 h-10 text-primary/40" />
              <LoginCard
                loginState={loginState}
                onStartLogin={startLogin}
                onSubmitCode={submitLoginCode}
              />
            </div>
          )}

          {(() => {
            // Track which subagent threads have been rendered inline
            const renderedParents = new Set<string>()
            return (
              <>
                {topLevelMessages.map((msg, i) => {
                  const isLast = i === topLevelMessages.length - 1
                  return (
                    <div key={i}>
                      <MessageBubble message={msg} isLast={isLast} isStreaming={isStreaming} />
                      {/* Render subagent threads for Agent/Task tool_use in this message */}
                      {msg.type === 'assistant' && (msg.message?.content || []).map(block => {
                        if (block.type !== 'tool_use' || !block.id) return null
                        if (block.name !== 'Agent' && block.name !== 'Task') return null
                        const kids = childrenByParent.get(block.id)
                        if (!kids || kids.length === 0) return null
                        renderedParents.add(block.id)
                        const info = agentMeta.get(block.id)
                        return (
                          <SubagentThread
                            key={block.id}
                            description={info?.description || 'Subagent'}
                            agentType={info?.agentType || ''}
                            messages={kids}
                            childrenByParent={childrenByParent}
                            agentMeta={agentMeta}
                            isStreaming={isStreaming}
                          />
                        )
                      })}
                    </div>
                  )
                })}
                {/* Render orphaned subagent threads (parent Agent block lost in merge) */}
                {Array.from(childrenByParent.entries()).map(([parentId, kids]) => {
                  if (renderedParents.has(parentId)) return null
                  const info = agentMeta.get(parentId)
                  return (
                    <SubagentThread
                      key={parentId}
                      description={info?.description || 'Subagent'}
                      agentType={info?.agentType || ''}
                      messages={kids}
                      childrenByParent={childrenByParent}
                      agentMeta={agentMeta}
                      isStreaming={isStreaming}
                    />
                  )
                })}
              </>
            )
          })()}

          {/* Streaming bubble — only show when no stored assistant message has the content yet.
              Once a non-final assistant message is stored (via --include-partial-messages),
              the stored message already renders the text, so we don't need a separate bubble. */}
          {(() => {
            const lastMsg = messages[messages.length - 1]
            const lastIsLiveAssistant = lastMsg?.type === 'assistant'
              && (lastMsg as { message?: { stop_reason?: string | null } }).message?.stop_reason == null
            return isStreaming && (streamingText || streamingThinking) && !lastIsLiveAssistant
          })() && (
            <div className="space-y-2">
              <div>
                {streamingThinking && (
                  <details className="group text-xs text-muted-foreground" open>
                    <summary className="flex items-center gap-1.5 cursor-pointer hover:text-foreground select-none">
                      <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                      Thinking...
                    </summary>
                    <pre className="mt-1 whitespace-pre-wrap">{streamingThinking}</pre>
                  </details>
                )}
                {smoothText && (
                  <div className="prose dark:prose-invert max-w-none prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {smoothText}
                    </Markdown>
                    <span className="inline-block w-0.5 h-4 bg-primary animate-pulse align-text-bottom ml-0.5" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Working indicator — pulsing dots when Claude is processing but no streaming text visible */}
          {isWorking && !(streamingText || streamingThinking) && !isCompacting && activeTools.size === 0 && messages.length > 0 && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5 px-3 py-2">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}

          {/* Stall warning — no response after 15s */}
          {stalled && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm space-y-1">
              <p className="text-yellow-600 dark:text-yellow-400">
                Response is taking longer than usual.
              </p>
              <p className="text-xs text-muted-foreground">
                Claude may be processing a complex request, or Anthropic may be experiencing issues.{' '}
                <a href="https://status.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Check Anthropic Status
                </a>
              </p>
            </div>
          )}

          {/* Pending Permission Requests */}
          {pendingPermissions.map((perm) => (
            <PermissionCard
              key={perm.request_id}
              request={perm}
              onRespond={respondToPermission}
            />
          ))}

          {/* Pending Questions (AskUserQuestion) */}
          {pendingQuestions.map((q) => (
            <QuestionCard
              key={q.request_id}
              request={q}
              onRespond={respondToQuestion}
            />
          ))}

          {/* Login Card — inline when there are already messages */}
          {messages.length > 0 && needsLogin && (
            <LoginCard
              loginState={loginState}
              onStartLogin={startLogin}
              onSubmitCode={submitLoginCode}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Tool Progress + Input Area */}
        <div className="border-t border-border">
          {/* Active tool progress bar */}
          {activeTools.size > 0 && (
            <ToolProgressBar activeTools={activeTools} />
          )}

          <div className="p-3">
            {/* Slash Command Menu */}
            {slashMenuVisible && (
              <div
                ref={slashMenuRef}
                className="mb-2 max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-lg scrollbar-thin"
              >
                {filteredCommands.map((cmd, i) => (
                  <button
                    key={cmd.name}
                    data-cmd-index={i}
                    onClick={() => selectCommand(cmd)}
                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-sm transition-colors ${
                      i === slashMenuIndex ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/50'
                    }`}
                  >
                    <Slash className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="font-medium">{cmd.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{cmd.type}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Image thumbnails */}
            {images.length > 0 && (
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={`data:${img.media_type};base64,${img.data}`}
                      alt={`Attachment ${i + 1}`}
                      className="w-12 h-12 rounded-lg object-cover border border-border"
                    />
                    <button
                      onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Remove image"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <form onSubmit={handleSubmit} className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!connected}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 disabled:opacity-50 transition-colors shrink-0"
                title="Attach image"
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                  <path d="M8 3v10M3 8h10" strokeLinecap="round" />
                </svg>
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onInput={handleTextareaInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={connected ? 'Message Claude...' : 'Connecting...'}
                disabled={!connected}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 min-h-[38px] max-h-[150px]"
              />
              {isWorking ? (
                <button
                  type="button"
                  onClick={interrupt}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                  title="Interrupt Claude"
                >
                  <Square className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!connected || !input.trim()}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  title="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </Panel>
  )
}

// ─── Tool Progress Bar ───────────────────────────────────────────────────────

function ToolProgressBar({ activeTools }: { activeTools: Map<string, ToolProgress> }) {
  // Group by tool name, show max elapsed per tool
  const toolSummaries = useMemo(() => {
    const byName = new Map<string, number>()
    for (const [, { toolName, elapsed }] of activeTools) {
      const current = byName.get(toolName) || 0
      if (elapsed > current) byName.set(toolName, elapsed)
    }
    return Array.from(byName.entries())
  }, [activeTools])

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground bg-secondary/20">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
      </span>
      {toolSummaries.map(([name, elapsed], i) => (
        <span key={name}>
          {i > 0 && <span className="text-border mx-1">&middot;</span>}
          <span className="font-medium">{name}</span>
          <span className="ml-1">{Math.round(elapsed)}s</span>
        </span>
      ))}
    </div>
  )
}

// ─── Welcome Banner ─────────────────────────────────────────────────────────

function WelcomeBanner({
  cliConnected,
  sessionState,
}: {
  cliConnected: boolean
  sessionState: { claude_code_version?: string; model?: string; cwd?: string; permissionMode?: string } | null
}) {
  if (!cliConnected || !sessionState) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Bot className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">Connecting to Claude...</p>
      </div>
    )
  }

  const rawVersion = sessionState.claude_code_version || ''
  // Strip "Claude Code" prefix and parens wrapper: "2.1.72 (Claude Code)" → "v2.1.72"
  const version = rawVersion.replace(/\s*\(Claude Code\)/i, '').replace(/^Claude Code\s*/i, '').trim()
  const model = sessionState.model || ''
  const cwd = sessionState.cwd || '~'
  // Shorten home dir paths
  const displayCwd = cwd.replace(/^\/home\/[^/]+/, '~').replace(/^\/Users\/[^/]+/, '~')

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 select-none">
      <div className="flex flex-col items-center gap-2">
        <Terminal className="w-8 h-8 text-primary/70" />
        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-foreground">
            Claude Code{version ? ` v${version.replace(/^v/i, '')}` : ''}
          </p>
          {model && <p className="text-xs text-muted-foreground">{model}</p>}
        </div>
      </div>
      <div className="text-xs text-muted-foreground/60 font-mono">{displayCwd}</div>
      <p className="text-xs text-muted-foreground max-w-xs text-center">
        Type a message below to start working, or use <kbd className="px-1 py-0.5 rounded bg-secondary text-foreground font-mono text-[10px]">/</kbd> for slash commands.
      </p>
    </div>
  )
}

// ─── Message Bubble Component ───────────────────────────────────────────────

function MessageBubble({ message, isLast, isStreaming }: { message: ChatMessage; isLast?: boolean; isStreaming?: boolean }) {
  if (message.type === 'user_message') {
    const msgImages = message.images
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg px-3 py-2 bg-primary/10 text-foreground space-y-2">
          {msgImages && msgImages.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {msgImages.map((img, i) => (
                <img
                  key={i}
                  src={`data:${img.media_type};base64,${img.data}`}
                  alt={`Attachment ${i + 1}`}
                  className="w-16 h-16 rounded object-cover border border-primary-foreground/20"
                />
              ))}
            </div>
          )}
          <pre className="whitespace-pre-wrap font-sans">{message.content}</pre>
        </div>
      </div>
    )
  }

  if (message.type === 'assistant') {
    const content = message.message?.content || []
    const grouped = groupContentBlocks(content)
    const isFinal = message.message?.stop_reason != null
    const showCursor = isLast && isStreaming && !isFinal
    return (
      <div className="space-y-2">
        {grouped.map((group, i) => (
          <ContentGroupView key={i} group={group} />
        ))}
        {showCursor && (
          <span className="inline-block w-0.5 h-4 bg-primary animate-pulse align-text-bottom ml-0.5" />
        )}
      </div>
    )
  }

  if (message.type === 'result') {
    const data = message.data
    // Show result text if present (slash command output, error messages)
    if (data.result) {
      return (
        <div>
          <div className="prose dark:prose-invert max-w-none prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2">
            <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {data.result}
            </Markdown>
          </div>
        </div>
      )
    }
    return (
      <div className="text-xs text-muted-foreground/60 px-1">
        {data.is_error ? 'Error' : 'Done'} — {(data.duration_ms / 1000).toFixed(1)}s
      </div>
    )
  }

  if (message.type === 'tool_use_summary') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 px-1">
        <Zap className="w-3 h-3" />
        <span>{message.summary}</span>
      </div>
    )
  }

  if (message.type === 'screenshot_result') {
    return <ScreenshotPreview filename={message.filename} mediaType={message.media_type} data={message.data} />
  }

  return null
}

// ─── Screenshot Preview ─────────────────────────────────────────────────────

function ScreenshotPreview({ filename, mediaType, data }: { filename: string; mediaType: string; data: string }) {
  const [expanded, setExpanded] = useState(false)

  // Handle stripped screenshots (data removed during persistence)
  if (!data) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <FileText className="w-3 h-3" />
        <span>{filename}</span>
        <span className="italic">(screenshot not available)</span>
      </div>
    )
  }

  const src = `data:${mediaType};base64,${data}`

  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <FileText className="w-3 h-3" />
        <span>{filename}</span>
      </div>
      {expanded ? (
        <img
          src={src}
          alt={filename}
          onClick={() => setExpanded(false)}
          className="rounded-lg border border-border max-w-full cursor-zoom-out"
        />
      ) : (
        <div
          onClick={() => setExpanded(true)}
          className="relative w-48 h-48 rounded-lg border border-border overflow-hidden cursor-zoom-in"
        >
          <img
            src={src}
            alt={filename}
            className="w-full object-cover object-top"
          />
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      )}
    </div>
  )
}

// ─── Tool Grouping ──────────────────────────────────────────────────────────

type ContentGroup = {
  type: 'single'
  block: ContentBlock
} | {
  type: 'tool_group'
  toolName: string
  items: { use: ContentBlock; result?: ContentBlock }[]
}

/** Group consecutive tool_use + tool_result pairs of the same tool name */
function groupContentBlocks(blocks: ContentBlock[]): ContentGroup[] {
  const groups: ContentGroup[] = []
  let i = 0

  while (i < blocks.length) {
    const block = blocks[i]

    // Skip hidden tool calls and their results entirely
    if (block.type === 'tool_use' && block.name && HIDDEN_TOOLS.has(block.name)) {
      const useId = block.id
      i++
      // Also skip the matching tool_result if present
      if (i < blocks.length && blocks[i].type === 'tool_result' && blocks[i].tool_use_id === useId) {
        i++
      }
      continue
    }

    if (block.type === 'tool_use' && block.name) {
      // Collect consecutive tool_use blocks of the same tool name.
      // For each, scan ahead for a matching tool_result (may not be immediately adjacent).
      const toolName = block.name
      const items: { use: ContentBlock; result?: ContentBlock }[] = []

      while (i < blocks.length && blocks[i].type === 'tool_use' && blocks[i].name === toolName) {
        const use = blocks[i]
        i++
        // Check if next block is a tool_result matching this tool_use
        let result: ContentBlock | undefined
        if (i < blocks.length && blocks[i].type === 'tool_result' && blocks[i].tool_use_id === use.id) {
          result = blocks[i]
          i++
        } else {
          // Scan ahead for a matching tool_result (may be separated by other blocks)
          for (let j = i; j < blocks.length; j++) {
            if (blocks[j].type === 'tool_result' && blocks[j].tool_use_id === use.id) {
              result = blocks[j]
              break
            }
          }
        }
        items.push({ use, result })
      }

      groups.push({ type: 'tool_group', toolName, items })
    } else {
      groups.push({ type: 'single', block })
      i++
    }
  }

  return groups
}

function ContentGroupView({ group }: { group: ContentGroup }) {
  if (group.type === 'single') {
    return <ContentBlockView block={group.block} />
  }

  // Tool group — single tool rendered inline, multiple get a count badge
  if (group.items.length === 1) {
    const { use, result } = group.items[0]
    return (
      <>
        <ToolUseView block={use} />
        {result && <ToolResultView block={result} toolName={use.name} />}
      </>
    )
  }

  return <CollapsedToolGroup toolName={group.toolName} items={group.items} />
}

function CollapsedToolGroup({ toolName, items }: { toolName: string; items: { use: ContentBlock; result?: ContentBlock }[] }) {
  const preview = items.map(({ use }) => formatToolSummary(toolName, use.input)).filter(Boolean).join(', ')

  return (
    <details className="group rounded border border-border/50 bg-secondary/20 text-xs">
      <summary className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/40 transition-colors">
        <ChevronRight className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="font-medium text-primary">{toolName}</span>
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold">
          {items.length}
        </span>
        {preview && <span className="text-muted-foreground truncate ml-1">{preview}</span>}
      </summary>
      <div className="space-y-1.5 px-2 pb-2 pt-1.5 border-t border-border/30">
        {items.map(({ use, result }, j) => (
          <div key={j} className="space-y-1">
            <ToolUseView block={use} />
            {result && <ToolResultView block={result} toolName={use.name} />}
          </div>
        ))}
      </div>
    </details>
  )
}

// ─── Content Block Views ────────────────────────────────────────────────────

function ContentBlockView({ block }: { block: ContentBlock }) {
  if (block.type === 'text' && block.text) {
    return (
      <div className="prose dark:prose-invert max-w-none prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5">
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
          {block.text}
        </Markdown>
      </div>
    )
  }

  if (block.type === 'tool_use') {
    return <ToolUseView block={block} />
  }

  if (block.type === 'tool_result') {
    return <ToolResultView block={block} />
  }

  if (block.type === 'thinking' && block.thinking) {
    const len = block.thinking.length
    const sizeLabel = len >= 1000 ? `${(len / 1000).toFixed(1)}k` : String(len)
    const preview = block.thinking.replace(/\s+/g, ' ').substring(0, 100)
    return (
      <details className="group text-xs text-muted-foreground">
        <summary className="flex items-center gap-1.5 cursor-pointer hover:text-foreground select-none">
          <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
          <span>Thinking</span>
          <span className="opacity-50">({sizeLabel} chars)</span>
          <span className="hidden group-[&:not([open])]:inline truncate opacity-40 ml-1">{preview}</span>
        </summary>
        <pre className="mt-1 whitespace-pre-wrap">{block.thinking}</pre>
      </details>
    )
  }

  return null
}

// Tools that are handled via interactive UI (QuestionCard) or have no user-visible output
const HIDDEN_TOOLS = new Set(['EnterPlanMode', 'ExitPlanMode', 'AskUserQuestion'])

function ToolUseView({ block }: { block: ContentBlock }) {
  // Hide plan mode tools — they're handled via QuestionCard or have no user-visible output
  if (block.name && HIDDEN_TOOLS.has(block.name)) return null

  const summary = formatToolSummary(block.name || '', block.input)
  const desc = typeof block.input?.description === 'string' ? block.input.description : ''
  const name = block.name || ''

  // Render tool-specific detail inside the collapsible
  const inp = block.input || {}
  const str = (key: string) => typeof inp[key] === 'string' ? inp[key] as string : ''
  const longDesc = desc && desc.length > 80 ? <div className="text-muted-foreground italic">{desc}</div> : null

  const renderDetail = () => {
    if (!block.input) return null

    switch (name) {
      case 'Edit': {
        const oldStr = str('old_string'), newStr = str('new_string')
        if (!oldStr && !newStr) break
        return (
          <div className="space-y-1.5">
            {longDesc}
            {!!inp.replace_all && (
              <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">replace all</span>
            )}
            <InlineDiff oldText={oldStr} newText={newStr} fileName={str('file_path')} />
          </div>
        )
      }
      case 'Write': {
        const writeContent = str('content')
        if (!writeContent) break
        const writePath = str('file_path')
        // Plan files — render as readable Markdown instead of a diff
        if (writePath.includes('.claude/plans/')) {
          return (
            <div className="space-y-1.5">
              <div className="text-muted-foreground/60 font-mono text-[11px]">{writePath.split('/').pop()}</div>
              <div className="prose dark:prose-invert max-w-none prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-p:my-1.5 prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 text-sm">
                <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                  {writeContent}
                </Markdown>
              </div>
            </div>
          )
        }
        return (
          <div className="space-y-1.5">
            {longDesc}
            <InlineDiff newText={writeContent} fileName={writePath} />
          </div>
        )
      }
      case 'Bash':
        if (!str('command')) break
        return (
          <div className="space-y-1.5">
            {longDesc}
            <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap font-mono bg-muted rounded px-2 py-1.5">
              <span className="text-muted-foreground/50 select-none">$ </span>{str('command')}
            </pre>
            {inp.timeout != null && <div className="text-muted-foreground/50">timeout: {String(inp.timeout)}ms</div>}
          </div>
        )
      case 'Read':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <code className="font-mono text-foreground/80">{str('file_path') || str('path')}</code>
            {(inp.offset != null || inp.limit != null) && (
              <div className="flex gap-3 text-muted-foreground">
                {inp.offset != null && <span>offset: <span className="text-foreground/70">{String(inp.offset)}</span></span>}
                {inp.limit != null && <span>limit: <span className="text-foreground/70">{String(inp.limit)}</span></span>}
              </div>
            )}
          </div>
        )
      case 'Glob':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <code className="font-mono bg-muted rounded px-1.5 py-0.5 text-foreground/80">{str('pattern')}</code>
            {str('path') && <div className="text-muted-foreground">in: <span className="font-mono text-foreground/70">{str('path')}</span></div>}
          </div>
        )
      case 'Grep':
        return (
          <div className="space-y-1.5">
            {longDesc}
            <pre className="font-mono bg-muted rounded px-2 py-1 text-foreground/80 overflow-x-auto whitespace-pre-wrap">{str('pattern')}</pre>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
              {str('path') && <span>path: <span className="font-mono text-foreground/70">{str('path')}</span></span>}
              {str('glob') && <span>glob: <span className="font-mono text-foreground/70">{str('glob')}</span></span>}
              {str('output_mode') && <span>mode: <span className="text-foreground/70">{str('output_mode')}</span></span>}
              {inp.context != null && <span>context: <span className="text-foreground/70">{String(inp.context)}</span></span>}
              {inp.head_limit != null && <span>limit: <span className="text-foreground/70">{String(inp.head_limit)}</span></span>}
            </div>
          </div>
        )
      case 'Agent':
        return (
          <div className="space-y-1.5">
            {str('description') && <div className="font-medium text-foreground/80">{str('description')}</div>}
            {str('subagent_type') && (
              <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">{str('subagent_type')}</span>
            )}
            {str('prompt') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                {str('prompt').substring(0, 2000)}
              </pre>
            )}
          </div>
        )
      case 'ToolSearch':
        return (
          <div className="space-y-1">
            {longDesc}
            <code className="font-mono text-foreground/80">{str('query')}</code>
          </div>
        )
      case 'WebSearch':
        return (
          <div className="space-y-1">
            {longDesc}
            <div className="font-medium text-foreground/80">{str('query')}</div>
            {Array.isArray(inp.allowed_domains) && (inp.allowed_domains as string[]).length > 0 && (
              <div className="text-muted-foreground">domains: {(inp.allowed_domains as string[]).join(', ')}</div>
            )}
          </div>
        )
      case 'WebFetch':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('url') && <code className="font-mono text-primary break-all">{str('url')}</code>}
            {str('prompt') && <div className="text-muted-foreground italic line-clamp-2">{str('prompt')}</div>}
          </div>
        )
      case 'TodoWrite': {
        const todos = Array.isArray(inp.todos) ? inp.todos as { content?: string; status?: string }[] : null
        if (!todos) break
        return (
          <div className="space-y-1">
            {todos.map((todo, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <span className="shrink-0 mt-0.5">
                  {todo.status === 'completed' ? '✓' : todo.status === 'in_progress' ? '◐' : '○'}
                </span>
                <span className={todo.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground/80'}>
                  {todo.content || JSON.stringify(todo)}
                </span>
              </div>
            ))}
          </div>
        )
      }
      case 'NotebookEdit':
        return (
          <div className="space-y-1.5">
            {str('notebook_path') && <code className="font-mono text-muted-foreground">{str('notebook_path')}</code>}
            <div className="flex gap-3 text-muted-foreground">
              {str('cell_type') && <span>type: <span className="text-foreground/70">{str('cell_type')}</span></span>}
              {str('edit_mode') && <span>mode: <span className="text-foreground/70">{str('edit_mode')}</span></span>}
              {inp.cell_number != null && <span>cell: <span className="text-foreground/70">{String(inp.cell_number)}</span></span>}
            </div>
            {str('new_source') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-40 overflow-y-auto">
                {str('new_source').substring(0, 2000)}
              </pre>
            )}
          </div>
        )
      case 'Skill':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('skill') && <div className="font-medium text-foreground/80">/{str('skill')}</div>}
            {str('args') && <div className="text-muted-foreground font-mono">{str('args')}</div>}
          </div>
        )
      case 'CronCreate':
        return (
          <div className="space-y-1.5">
            {longDesc}
            {str('cron') && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">schedule:</span>
                <code className="font-mono bg-muted rounded px-1.5 py-0.5 text-foreground/80">{str('cron')}</code>
              </div>
            )}
            {str('prompt') && <div className="text-foreground/80">{str('prompt')}</div>}
            {inp.recurring != null && (
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${inp.recurring ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                {inp.recurring ? 'recurring' : 'one-shot'}
              </span>
            )}
          </div>
        )
      case 'CronDelete':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
          </div>
        )
      case 'CronList':
        return <div className="text-muted-foreground">{longDesc || 'List scheduled tasks'}</div>
      case 'TaskCreate':
      case 'TaskUpdate':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('description') && <div className="text-foreground/80">{str('description')}</div>}
            {str('status') && (
              <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${
                str('status') === 'completed' ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : str('status') === 'in_progress' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                  : str('status') === 'blocked' ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {str('status')}
              </span>
            )}
            {str('prompt') && (
              <pre className="font-mono bg-muted rounded px-2 py-1.5 text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto scrollbar-thin">
                {str('prompt').substring(0, 1000)}
              </pre>
            )}
          </div>
        )
      case 'TaskGet':
      case 'TaskList':
      case 'TaskStop':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
          </div>
        )
      case 'TaskOutput':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('id') && <div className="text-muted-foreground">id: <code className="font-mono text-foreground/80">{str('id')}</code></div>}
            {inp.offset != null && <div className="text-muted-foreground">offset: <span className="text-foreground/70">{String(inp.offset)}</span></div>}
          </div>
        )
      case 'LSP':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('command') && <code className="font-mono text-foreground/80">{str('command')}</code>}
            {str('file_path') && <div className="text-muted-foreground font-mono">{str('file_path')}</div>}
          </div>
        )
      case 'EnterWorktree':
        return (
          <div className="space-y-1">
            {longDesc}
            {str('branch') && <div className="text-muted-foreground">branch: <code className="font-mono text-foreground/80">{str('branch')}</code></div>}
          </div>
        )
      case 'SendMessage':
        return (
          <div className="space-y-1">
            {str('recipient') && <div className="text-muted-foreground">to: <span className="font-medium text-foreground/80">{str('recipient')}</span></div>}
            {str('content') && <div className="text-foreground/80 whitespace-pre-wrap">{str('content').substring(0, 500)}</div>}
          </div>
        )
    }

    // Fallback: raw JSON for unknown tools
    return (
      <div className="space-y-1.5">
        {longDesc}
        <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(block.input, null, 2).substring(0, 1000)}
        </pre>
      </div>
    )
  }

  // Auto-expand plan file writes so the plan content is visible
  const autoExpand = name === 'Write' && typeof inp.file_path === 'string' && (inp.file_path as string).includes('.claude/plans/')

  return (
    <details className="group rounded border border-border/50 bg-secondary/20 text-xs" open={autoExpand || undefined}>
      <summary className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/40 transition-colors">
        <ChevronRight className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="font-medium text-primary">{name}</span>
        {summary && <span className="text-muted-foreground truncate">{summary}</span>}
      </summary>
      <div className="border-t border-border/30 pt-1.5 mx-2 mb-1">
        {renderDetail()}
      </div>
    </details>
  )
}

// ─── Inline Diff Viewer ──────────────────────────────────────────────────────

interface DiffLine {
  type: 'add' | 'del' | 'context'
  content: string
  wordChanges?: { value: string; added?: boolean; removed?: boolean }[]
}

/** Simple Myers-like diff on lines, returning edit script */
function diffLines(oldLines: string[], newLines: string[]): DiffLine[] {
  // Build LCS table
  const m = oldLines.length, n = newLines.length
  // For very large files, skip LCS and just show all del + all add
  if (m + n > 2000) {
    return [
      ...oldLines.map(l => ({ type: 'del' as const, content: l })),
      ...newLines.map(l => ({ type: 'add' as const, content: l })),
    ]
  }
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldLines[i - 1] === newLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  // Backtrack to produce diff
  const result: DiffLine[] = []
  let i = m, j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.push({ type: 'context', content: oldLines[i - 1] })
      i--; j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: 'add', content: newLines[j - 1] })
      j--
    } else {
      result.push({ type: 'del', content: oldLines[i - 1] })
      i--
    }
  }
  result.reverse()
  return result
}

/** Word-level diff between two strings */
function diffWords(oldStr: string, newStr: string): { value: string; added?: boolean; removed?: boolean }[] {
  const splitWords = (s: string) => s.match(/\S+|\s+/g) || []
  const oldW = splitWords(oldStr), newW = splitWords(newStr)
  if (oldW.length + newW.length > 500) return [{ value: oldStr, removed: true }, { value: newStr, added: true }]

  const m = oldW.length, n = newW.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldW[i - 1] === newW[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])

  const parts: { value: string; added?: boolean; removed?: boolean }[] = []
  let ii = m, jj = n
  while (ii > 0 || jj > 0) {
    if (ii > 0 && jj > 0 && oldW[ii - 1] === newW[jj - 1]) {
      parts.push({ value: oldW[ii - 1] })
      ii--; jj--
    } else if (jj > 0 && (ii === 0 || dp[ii][jj - 1] >= dp[ii - 1][jj])) {
      parts.push({ value: newW[jj - 1], added: true })
      jj--
    } else {
      parts.push({ value: oldW[ii - 1], removed: true })
      ii--
    }
  }
  parts.reverse()
  // Merge consecutive same-type parts
  const merged: typeof parts = []
  for (const p of parts) {
    const last = merged[merged.length - 1]
    if (last && last.added === p.added && last.removed === p.removed) {
      last.value += p.value
    } else {
      merged.push({ ...p })
    }
  }
  return merged
}

function computeDiffLines(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')
  const raw = diffLines(oldLines, newLines)

  // Filter to show only changed lines + 3 lines of context
  const changed = new Set<number>()
  raw.forEach((line, idx) => { if (line.type !== 'context') changed.add(idx) })
  const visible = new Set<number>()
  for (const idx of changed) {
    for (let k = Math.max(0, idx - 3); k <= Math.min(raw.length - 1, idx + 3); k++) {
      visible.add(k)
    }
  }
  const lines = raw.filter((_, idx) => visible.has(idx))

  // Add word-level highlighting for adjacent del/add pairs
  let i = 0
  while (i < lines.length) {
    const delStart = i
    while (i < lines.length && lines[i].type === 'del') i++
    const delEnd = i
    const addStart = i
    while (i < lines.length && lines[i].type === 'add') i++
    const addEnd = i

    const delCount = delEnd - delStart
    const addCount = addEnd - addStart
    if (delCount > 0 && addCount > 0) {
      const pairs = Math.min(delCount, addCount)
      for (let j = 0; j < pairs; j++) {
        const wd = diffWords(lines[delStart + j].content, lines[addStart + j].content)
        lines[delStart + j].wordChanges = wd.filter(p => !p.added)
        lines[addStart + j].wordChanges = wd.filter(p => !p.removed)
      }
    }
    if (i === delStart) i++
  }

  return lines
}

function InlineDiff({
  oldText,
  newText,
  fileName,
}: {
  oldText?: string
  newText: string
  fileName?: string
}) {
  const lines: DiffLine[] = useMemo(() => {
    if (oldText != null && oldText !== '') {
      return computeDiffLines(oldText, newText)
    }
    // Write (new file) — all lines are additions
    return newText.split('\n').map(line => ({ type: 'add' as const, content: line }))
  }, [oldText, newText])

  // Truncate long diffs
  const MAX_LINES = 60
  const truncated = lines.length > MAX_LINES
  const visibleLines = truncated ? lines.slice(0, MAX_LINES) : lines

  const shortName = fileName ? (fileName.split('/').pop() || fileName) : ''
  const dirPath = fileName && shortName !== fileName ? fileName.slice(0, fileName.length - shortName.length) : ''

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-muted max-h-[280px] overflow-y-auto font-mono text-[12px] leading-[1.5] scrollbar-thin">
      {fileName && (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-secondary/30 border-b border-border text-[11px] sticky top-0 z-[1]">
          <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
          {dirPath && <span className="text-muted-foreground">{dirPath}</span>}
          <span className="font-semibold text-foreground">{shortName}</span>
        </div>
      )}
      <div>
        {visibleLines.map((line, i) => (
          <div
            key={i}
            className={
              line.type === 'add'
                ? 'bg-green-500/10 dark:bg-green-500/8'
                : line.type === 'del'
                  ? 'bg-red-500/10 dark:bg-red-500/8'
                  : ''
            }
            style={{ display: 'flex', minHeight: 20 }}
          >
            <span
              className={`shrink-0 w-4 text-center select-none ${
                line.type === 'add'
                  ? 'text-green-600 dark:text-green-400'
                  : line.type === 'del'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground/30'
              }`}
            >
              {line.type === 'add' ? '+' : line.type === 'del' ? '-' : ' '}
            </span>
            <span className="flex-1 min-w-0 whitespace-pre-wrap break-all pr-3 text-foreground/80">
              {line.wordChanges ? (
                line.wordChanges.map((part, j) => {
                  if (part.added) return <span key={j} className="bg-green-500/25 dark:bg-green-400/20 rounded-sm px-px">{part.value}</span>
                  if (part.removed) return <span key={j} className="bg-red-500/25 dark:bg-red-400/20 rounded-sm px-px">{part.value}</span>
                  return <span key={j}>{part.value}</span>
                })
              ) : (
                line.content || '\u00A0'
              )}
            </span>
          </div>
        ))}
        {truncated && (
          <div className="px-3 py-1 text-muted-foreground text-[11px] border-t border-border/30">
            ... {lines.length - MAX_LINES} more lines
          </div>
        )}
      </div>
    </div>
  )
}

function ToolResultView({ block, toolName }: { block: ContentBlock; toolName?: string }) {
  const content = typeof block.content === 'string' ? block.content : JSON.stringify(block.content)
  const label = block.is_error ? 'Error' : 'Result'
  const [expanded, setExpanded] = useState(false)

  const allLines = (content || '').split('\n')
  const totalLines = allLines.length
  const TAIL_LINES = 20
  // Don't abbreviate Edit/Write results — the diff in ToolUseView is the primary display
  const skipAbbreviation = toolName === 'Edit' || toolName === 'Write'
  const isTruncatable = !skipAbbreviation && totalLines > TAIL_LINES
  const displayText = isTruncatable && !expanded
    ? allLines.slice(-TAIL_LINES).join('\n')
    : content || ''

  return (
    <details className="group rounded border border-border/50 bg-secondary/10 text-xs">
      <summary className={`flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/30 transition-colors ${block.is_error ? 'text-destructive' : 'text-muted-foreground'}`}>
        <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
        <span>{label}</span>
        {isTruncatable && <span className="opacity-50">{totalLines} lines</span>}
        <span className="truncate opacity-70">{(content || '').substring(0, 80)}</span>
      </summary>
      <div className="border-t border-border/30 pt-1.5 mx-2 mb-1">
        {isTruncatable && !expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(true) }}
            className="text-primary hover:underline mb-1"
          >
            Show all {totalLines} lines
          </button>
        )}
        {isTruncatable && expanded && (
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(false) }}
            className="text-primary hover:underline mb-1"
          >
            Show last {TAIL_LINES} lines
          </button>
        )}
        <pre className="text-muted-foreground overflow-x-auto whitespace-pre-wrap">
          {displayText}
        </pre>
      </div>
    </details>
  )
}

/** Generate a short inline summary for a tool call — prefers description when short enough */
function formatToolSummary(name: string, input?: Record<string, unknown>): string {
  if (!input) return ''
  // Prefer input.description if available and short enough (like reference app)
  const desc = typeof input.description === 'string' ? input.description : ''
  if (desc && desc.length <= 80) return desc
  switch (name) {
    case 'Bash':
      return typeof input.command === 'string' ? input.command.substring(0, 60) : desc
    case 'Read':
      return typeof input.file_path === 'string' ? input.file_path.split('/').pop() || '' : desc
    case 'Edit':
    case 'Write':
      return typeof input.file_path === 'string' ? input.file_path.split('/').pop() || '' : desc
    case 'Grep':
      return typeof input.pattern === 'string' ? `/${input.pattern}/` : desc
    case 'Glob':
      return typeof input.pattern === 'string' ? input.pattern : desc
    case 'Agent':
      return desc
    case 'ToolSearch':
      return typeof input.query === 'string' ? input.query : desc
    case 'TodoWrite':
      return desc
    case 'CronCreate':
      return typeof input.cron === 'string' ? input.cron : desc
    case 'CronDelete':
    case 'TaskGet':
    case 'TaskStop':
    case 'TaskOutput':
      return typeof input.id === 'string' ? input.id : desc
    case 'TaskCreate':
    case 'TaskUpdate':
      return desc || (typeof input.status === 'string' ? input.status : '')
    case 'LSP':
      return typeof input.command === 'string' ? input.command : desc
    case 'SendMessage':
      return typeof input.recipient === 'string' ? `to ${input.recipient}` : desc
    case 'Skill':
      return typeof input.skill === 'string' ? `/${input.skill}` : desc
    default:
      return desc
  }
}

// ─── Login Card Component ────────────────────────────────────────────────────

function LoginCard({
  loginState,
  onStartLogin,
  onSubmitCode,
}: {
  loginState: LoginState
  onStartLogin: () => void
  onSubmitCode?: (code: string) => void
}) {
  // Login complete — briefly show success, then hide
  if (loginState.status === 'complete') {
    return (
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
          <Check className="w-4 h-4" />
          Logged in successfully
        </div>
      </div>
    )
  }

  // Awaiting URL from bridge
  if (loginState.status === 'awaiting_url') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <Loader2 className="w-4 h-4 animate-spin" />
          Starting login...
        </div>
      </div>
    )
  }

  // Have URL — show link + code paste input
  if ((loginState.status === 'awaiting_code' || loginState.status === 'submitting') && loginState.url) {
    return <LoginCodeCard url={loginState.url} submitting={loginState.status === 'submitting'} onSubmitCode={onSubmitCode!} />
  }

  // Submitting code
  if (loginState.status === 'submitting') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <Loader2 className="w-4 h-4 animate-spin" />
          Authenticating...
        </div>
      </div>
    )
  }

  // Initial state or error — show login button
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <LogIn className="w-4 h-4" />
        Authentication required
      </div>
      <p className="text-sm text-muted-foreground">
        Sign in with your Anthropic account to use Claude Code.
      </p>
      {loginState.status === 'error' && loginState.error && loginState.error !== 'not_authenticated' && (
        <p className="text-sm text-destructive">{loginState.error}</p>
      )}
      <button
        onClick={onStartLogin}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        Sign in
      </button>
    </div>
  )
}

function LoginCodeCard({ url, submitting, onSubmitCode }: { url: string; submitting: boolean; onSubmitCode: (code: string) => void }) {
  const [code, setCode] = useState('')

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <Key className="w-4 h-4" />
        Sign in with your Claude subscription
      </div>

      <div className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          1. Click below to open the Anthropic login page
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Anthropic Login
        </a>
        <p className="text-muted-foreground">
          2. After logging in, paste the code shown on the page:
        </p>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.trim() && !submitting) onSubmitCode(code.trim())
          }}
          placeholder="Paste authorization code..."
          disabled={submitting}
          className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          autoFocus
        />
        <button
          onClick={() => { if (code.trim()) onSubmitCode(code.trim()) }}
          disabled={!code.trim() || submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          {submitting ? 'Verifying...' : 'Submit'}
        </button>
      </div>
    </div>
  )
}

// ─── Subagent Thread ────────────────────────────────────────────────────────

function SubagentThread({
  description,
  agentType,
  messages: kids,
  childrenByParent,
  agentMeta,
  isStreaming,
}: {
  description: string
  agentType: string
  messages: ChatMessage[]
  childrenByParent: Map<string, ChatMessage[]>
  agentMeta: Map<string, { description: string; agentType: string }>
  isStreaming: boolean
}) {
  // Derive status from children: has result → completed, has assistant → running
  const lastKid = kids[kids.length - 1]
  const status = lastKid?.type === 'result'
    ? (lastKid.data?.is_error ? 'failed' : 'completed')
    : (kids.some(m => m.type === 'assistant') ? 'running' : 'pending')

  // Auto-expand when running, collapse when completed
  const [open, setOpen] = useState(status === 'running' || status === 'pending')

  const statusColors: Record<string, string> = {
    running: 'text-blue-500',
    completed: 'text-green-500',
    failed: 'text-red-500',
    pending: 'text-muted-foreground/40',
  }

  // Preview: last text content when collapsed
  const preview = !open ? (() => {
    for (let i = kids.length - 1; i >= 0; i--) {
      const m = kids[i]
      if (m.type === 'assistant') {
        const text = m.message?.content?.find((b: ContentBlock) => b.type === 'text')?.text
        if (text) return text.substring(0, 60) + (text.length > 60 ? '...' : '')
      }
    }
    return ''
  })() : ''

  return (
    <div className="ml-4 border-l-2 border-primary/20 pl-3 my-2">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-1.5 py-1 text-left cursor-pointer group w-full"
      >
        <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
        <span className="text-xs font-medium text-primary/70">{description}</span>
        {agentType && <span className="text-[10px] text-muted-foreground/50">{agentType}</span>}
        <span className={`text-[10px] ${statusColors[status]}`}>
          {status === 'running' ? '●' : status === 'completed' ? '✓' : status === 'failed' ? '✗' : '○'}
        </span>
        {!open && preview && (
          <span className="text-[10px] text-muted-foreground/40 truncate ml-1">{preview}</span>
        )}
        <span className="text-[10px] text-muted-foreground/40 ml-auto shrink-0">{kids.length}</span>
      </button>
      {open && (
        <div className="space-y-2 pb-2 mt-1">
          {kids.map((msg, j) => (
            <div key={j}>
              <MessageBubble message={msg} isLast={j === kids.length - 1} isStreaming={isStreaming} />
              {/* Recursive: nested subagent threads */}
              {msg.type === 'assistant' && (msg.message?.content || []).map(block => {
                if (block.type !== 'tool_use' || block.name !== 'Agent' || !block.id) return null
                const nested = childrenByParent.get(block.id)
                if (!nested || nested.length === 0) return null
                const info = agentMeta.get(block.id)
                return (
                  <SubagentThread
                    key={block.id}
                    description={info?.description || 'Subagent'}
                    agentType={info?.agentType || ''}
                    messages={nested}
                    childrenByParent={childrenByParent}
                    agentMeta={agentMeta}
                    isStreaming={isStreaming}
                  />
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Permission Card Component ──────────────────────────────────────────────

function PermissionCard({
  request,
  onRespond,
}: {
  request: PermissionRequest
  onRespond: (requestId: string, behavior: 'allow' | 'deny') => void
}) {
  return (
    <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-yellow-600 dark:text-yellow-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Permission Request
      </div>
      <div className="text-sm">
        <span className="font-medium">{request.tool_name}</span>
        {request.description && (
          <p className="text-muted-foreground mt-1">{request.description}</p>
        )}
        {request.input && Object.keys(request.input).length > 0 && (
          <pre className="mt-2 text-xs bg-secondary/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(request.input, null, 2).substring(0, 500)}
          </pre>
        )}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onRespond(request.request_id, 'allow')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Allow
        </button>
        <button
          onClick={() => onRespond(request.request_id, 'deny')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Deny
        </button>
      </div>
    </div>
  )
}

// ─── Exit Plan Mode Card ────────────────────────────────────────────────────

function ExitPlanModeCard({
  requestId,
  description,
  onRespond,
}: {
  requestId: string
  description?: string
  onRespond: (requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => void
}) {
  const [showRevise, setShowRevise] = useState(false)
  const [reviseText, setReviseText] = useState('')

  const handleAccept = () => {
    onRespond(requestId, { '0': 'Yes, proceed with the plan' })
  }

  const handleRevise = () => {
    if (!reviseText.trim()) return
    onRespond(requestId, { '0': reviseText.trim() }, 'deny')
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <MessageCircleQuestion className="w-4 h-4" />
        Plan ready
      </div>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <p className="text-sm">Would you like to proceed?</p>

      <div className="space-y-1.5">
        {/* Accept option */}
        <button
          onClick={handleAccept}
          className="w-full text-left px-3 py-2 rounded-lg border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all"
        >
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
              1
            </span>
            <span className="text-sm font-medium">Yes, proceed with the plan</span>
          </div>
        </button>

        {/* Revise option */}
        <button
          onClick={() => setShowRevise(!showRevise)}
          className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
            showRevise
              ? 'border-primary/30 bg-primary/5'
              : 'border-border bg-card/50 hover:bg-card hover:border-primary/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
              2
            </span>
            <span className="text-sm font-medium">Tell Claude what to change</span>
          </div>
        </button>

        {showRevise && (
          <div className="flex gap-2 pl-7">
            <input
              type="text"
              value={reviseText}
              onChange={(e) => setReviseText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && reviseText.trim()) handleRevise()
              }}
              placeholder="Describe what to change..."
              className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <button
              onClick={handleRevise}
              disabled={!reviseText.trim()}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              Send
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Question Card Component (AskUserQuestion) ─────────────────────────────

function QuestionCard({
  request,
  onRespond,
}: {
  request: QuestionRequest
  onRespond: (requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => void
}) {
  const [customText, setCustomText] = useState('')
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const questions: QuestionItem[] = Array.isArray(request.input.questions) ? request.input.questions : []
  const toolName = request.tool_name || ''

  // ExitPlanMode — show plan acceptance with options matching vanilla CLI
  if (toolName === 'ExitPlanMode') {
    return (
      <ExitPlanModeCard
        requestId={request.request_id}
        description={request.description}
        onRespond={onRespond}
      />
    )
  }

  // Single question string fallback
  if (questions.length === 0 && typeof request.input.question === 'string') {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <MessageCircleQuestion className="w-4 h-4" />
          Claude is asking
        </div>
        <p className="text-sm">{request.input.question}</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customText.trim()) {
                onRespond(request.request_id, { '0': customText.trim() })
              }
            }}
            placeholder="Type your answer..."
            className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
          <button
            onClick={() => {
              if (customText.trim()) onRespond(request.request_id, { '0': customText.trim() })
            }}
            disabled={!customText.trim()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    )
  }

  // No questions at all — fallback with text input
  if (questions.length === 0) {
    const desc = request.description || ''
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <MessageCircleQuestion className="w-4 h-4" />
          Claude is asking
        </div>
        {desc && <p className="text-sm">{desc}</p>}
        <div className="flex gap-2">
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customText.trim()) {
                onRespond(request.request_id, { '0': customText.trim() })
              }
            }}
            placeholder="Type your answer..."
            className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
          <button
            onClick={() => {
              if (customText.trim()) onRespond(request.request_id, { '0': customText.trim() })
            }}
            disabled={!customText.trim()}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    )
  }

  // Multi-question: collect all answers before sending
  const handleSelect = (index: number, label: string) => {
    const newAnswers = { ...answers, [String(index)]: label }
    setAnswers(newAnswers)
    // Auto-submit when all questions are answered
    if (Object.keys(newAnswers).length === questions.length) {
      onRespond(request.request_id, newAnswers)
    }
  }

  const answeredCount = Object.keys(answers).length

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <MessageCircleQuestion className="w-4 h-4" />
        Claude is asking
        {questions.length > 1 && (
          <span className="text-xs text-muted-foreground font-normal">
            ({answeredCount}/{questions.length})
          </span>
        )}
      </div>
      {questions.map((q, i) => {
        const answered = answers[String(i)]
        return (
          <QuestionDisplay
            key={i}
            question={q}
            answered={answered}
            onSelect={(label) => handleSelect(i, label)}
          />
        )
      })}
    </div>
  )
}

function QuestionDisplay({
  question,
  answered,
  onSelect,
}: {
  question: QuestionItem
  answered?: string
  onSelect: (label: string) => void
}) {
  const [customText, setCustomText] = useState('')
  const [showCustom, setShowCustom] = useState(false)

  // Already answered — show selected answer
  if (answered) {
    return (
      <div className="space-y-2 opacity-60">
        {question.header && (
          <span className="inline-block text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
            {question.header}
          </span>
        )}
        {question.question && (
          <p className="text-sm leading-relaxed">{question.question}</p>
        )}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/10">
          <Check className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-medium">{answered}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {question.header && (
        <span className="inline-block text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
          {question.header}
        </span>
      )}
      {question.question && (
        <p className="text-sm leading-relaxed">{question.question}</p>
      )}
      {question.options.length > 0 && (
        <div className="space-y-1.5">
          {question.options.map((opt, j) => {
            const label = typeof opt === 'string' ? opt : (opt.label || String(opt))
            const desc = typeof opt === 'object' ? opt.description : undefined
            return (
              <button
                key={j}
                onClick={() => onSelect(label)}
                className="w-full text-left px-3 py-2 rounded-lg border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs font-medium text-muted-foreground">
                    {j + 1}
                  </span>
                  <div>
                    <span className="text-sm font-medium">{label}</span>
                    {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
                  </div>
                </div>
              </button>
            )
          })}

          {/* Custom answer option */}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
              showCustom
                ? 'border-primary/30 bg-primary/5'
                : 'border-border bg-card/50 hover:bg-card hover:border-primary/30'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center shrink-0 text-xs text-muted-foreground">
                ?
              </span>
              <span className="text-sm text-muted-foreground">Type something else...</span>
            </div>
          </button>

          {showCustom && (
            <div className="flex gap-2 pl-7">
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customText.trim()) {
                    onSelect(customText.trim())
                  }
                }}
                placeholder="Type your answer..."
                className="flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <button
                onClick={() => {
                  if (customText.trim()) onSelect(customText.trim())
                }}
                disabled={!customText.trim()}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
